import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { faArrowRotateBack } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

import { pluralize } from '~libs/stringarly';

import Button from '~components/Button';
import ClickableIcon from '~components/ClickableIcon';
import { faCheck } from '~components/icon';
import Modal from '~components/Modal';

import { clearError } from '~appointments/reducers/appointments';
import { clearSimulated as clearSimulatedClients } from '~weekly-planner/reducers/clients/clients';
import { clear, clearInteracting } from '~weekly-planner/reducers/unsaved';
import { clearSimulated as clearSimulatedUsers } from '~weekly-planner/reducers/users/users';

import { selectAllChangesCount } from '~weekly-planner/selectors/unsaved';

import { Changes } from '../modal';

interface ComponentProps {
  date: string;
  onRefresh?: (searchParams?: any, force?: boolean) => void;
}

export const ChangesPill: React.FC<ComponentProps> = ({ onRefresh, date }) => {
  const dispatch = useAppDispatch();

  const [isChangesModalOpen, setIsChangesModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const changesCount = useAppSelector(selectAllChangesCount);

  const statusText = (() => {
    if (changesCount === 0) {
      return 'No pending changes';
    } else if (changesCount > 0) {
      return `${changesCount} ${pluralize(changesCount, 'Change').pluralNoun} pending`;
    }
  })();

  const className = classNames('pill changes', {
    pending: changesCount > 0,
  });

  const handleReset = () => {
    // Reset unsaved changes
    dispatch(clearInteracting());
    dispatch(clear());
    dispatch(clearSimulatedUsers([]));
    dispatch(clearSimulatedClients([]));

    setIsResetModalOpen(false);

    if (onRefresh) {
      onRefresh(undefined, true);
    }
  };

  return (
    <>
      <div className={className} data-cy="weekly-planner-changes-pill">
        {statusText}
      </div>
      <div className="d-flex align-items-center justify-content-center">
        <ClickableIcon
          id={'changes-pill-save-icon'}
          icon={faCheck}
          className="change-icon confirm"
          onClick={() => setIsChangesModalOpen(true)}
          disabled={changesCount === 0}
          color={null}
        />
        <ClickableIcon
          id={'changes-pill-reset-icon'}
          icon={faArrowRotateBack}
          className="change-icon reset"
          onClick={() => setIsResetModalOpen(true)}
          disabled={changesCount === 0}
          color={null}
        />
      </div>
      <Changes
        isOpen={isChangesModalOpen}
        onClose={() => {
          dispatch(clearError());
          setIsChangesModalOpen(false);
          onRefresh && onRefresh(undefined, true);
        }}
        weekStart={date}
      />
      <Modal
        isOpen={isResetModalOpen}
        setIsOpen={setIsResetModalOpen}
        header="Confirm Reset"
        size="md"
        centered
        footer={
          <>
            <Button color="secondary" onClick={() => setIsResetModalOpen(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleReset} data-cy="weekly-planner-reset-confirm">
              Reset
            </Button>
          </>
        }
      >
        <p>Are you sure you want to reset the planner? Any unsaved changes will be lost.</p>
      </Modal>
    </>
  );
};

export default ChangesPill;
